import { numFont } from '../../utils/fonts';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Snackbar,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { useUserStore } from "store";

import DataTable from "../shared/DataTable";
import ColorSwatchInput, { PRESET_COLORS } from "../shared/ColorSwatchInput";
import { CALENDAR_STATE_KEYS, getStateConfig } from "../calendar/calendarColors";
import { STATUS_CONFIG } from "../calendar/calendarConstants";
import { getCsrfToken } from "../../utils";
import { can } from "../../utils/access";

// "Kalenteri" sub-tab of Ominaisuudet: the colour of each work order state on calendar cards,
// and the organization's colour tags (väritunnisteet).

const TAG_MODE_FILL = "fill";
const TAG_MODE_BORDER = "border";

const emptyTagForm = { name: "", color: PRESET_COLORS[0], display_mode: TAG_MODE_FILL, is_active: true };

// Mirrors the calendar's own rule: display_order first, and an employee who has never been
// ordered (0) sits at the end in alphabetical order.
const sortForCalendar = (employees) =>
  [...employees].sort((a, b) => {
    const aOrder = a.display_order || Number.MAX_SAFE_INTEGER;
    const bOrder = b.display_order || Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return (a.name || "").localeCompare(b.name || "", "fi");
  });

function CalendarSettingsTab({ t, onDirtyChange }) {
  const { currentOrganization, patchCurrentOrganization } = useUserStore();
  const orgId = currentOrganization?.organization_id;

  const [colors, setColors] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [tags, setTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [tagDialog, setTagDialog] = useState(null); // { tag | null, form }
  const [tagSaving, setTagSaving] = useState(false);
  const [tagError, setTagError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canManageTeam = can(currentOrganization, "team.manage");
  const [calendarEmployees, setCalendarEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [reordering, setReordering] = useState(false);

  useEffect(() => {
    setColors(currentOrganization?.calendar_state_colors || {});
    setIsDirty(false);
  }, [currentOrganization?.organization_id, currentOrganization?.calendar_state_colors]);

  const loadTags = useCallback(async () => {
    if (!orgId) return;
    try {
      setTagsLoading(true);
      const response = await axios.get(`/api/color-tags/?organization=${orgId}&show_inactive=true`);
      setTags(response.data || []);
    } catch (error) {
      setSnackbar({ open: true, message: t("calendarSettings.tags.loadFailed"), severity: "error" });
    } finally {
      setTagsLoading(false);
    }
  }, [orgId, t]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  // --- employee column order -----------------------------------------------------------

  const loadCalendarEmployees = useCallback(async () => {
    if (!orgId || !canManageTeam) return;
    try {
      setEmployeesLoading(true);
      const response = await axios.get(`/api/employees/?organization=${orgId}`);
      const data = response.data?.results || response.data || [];
      setCalendarEmployees(sortForCalendar(data.filter((emp) => emp.show_in_calendar !== false)));
    } catch (error) {
      setSnackbar({ open: true, message: t("calendarSettings.employeeOrder.loadFailed"), severity: "error" });
    } finally {
      setEmployeesLoading(false);
    }
  }, [orgId, canManageTeam, t]);

  useEffect(() => {
    loadCalendarEmployees();
  }, [loadCalendarEmployees]);

  /**
   * Move one employee a step up or down and persist the whole row.
   *
   * Sends the complete id list rather than a pairwise swap: the endpoint rewrites
   * display_order in one atomic pass, so a dropped request can never leave the calendar
   * half-ordered. Applies optimistically and reloads on failure.
   */
  const moveEmployee = async (index, direction) => {
    if (reordering) return;
    const target = index + direction;
    if (target < 0 || target >= calendarEmployees.length) return;

    const reordered = [...calendarEmployees];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setCalendarEmployees(reordered);

    setReordering(true);
    try {
      const response = await axios.post(
        `/api/employees/reorder/?organization=${orgId}`,
        { employees: reordered.map((emp) => emp.id) },
        { headers: { "X-CSRFToken": getCsrfToken(), "X-Organization-Id": orgId } }
      );
      setCalendarEmployees(sortForCalendar(response.data || reordered));
    } catch (error) {
      setSnackbar({ open: true, message: t("calendarSettings.employeeOrder.saveFailed"), severity: "error" });
      await loadCalendarEmployees();
    } finally {
      setReordering(false);
    }
  };

  // --- state colours -------------------------------------------------------------------

  const setStateColor = (state, hex) => {
    setColors((prev) => ({ ...prev, [state]: hex }));
    setIsDirty(true);
  };

  const resetStateColor = (state) => {
    setColors((prev) => {
      const next = { ...prev };
      delete next[state];
      return next;
    });
    setIsDirty(true);
  };

  const handleSaveColors = async () => {
    if (!orgId) return;
    try {
      setSaving(true);
      const response = await axios.patch(
        `/api/organizations/${orgId}/`,
        { calendar_state_colors: colors },
        { headers: { "X-CSRFToken": getCsrfToken(), "X-Organization-Id": orgId } }
      );
      // Patch the store rather than refetching the user: the calendar reads the colours
      // straight from currentOrganization, so this repaints it immediately.
      patchCurrentOrganization({
        calendar_state_colors: response.data?.calendar_state_colors || colors,
      });
      setIsDirty(false);
      setSnackbar({ open: true, message: t("calendarSettings.stateColors.saved"), severity: "success" });
    } catch (error) {
      const message = error.response?.data?.detail || t("calendarSettings.stateColors.saveFailed");
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRef = useRef(handleSaveColors);
  handleSaveRef.current = handleSaveColors;

  useEffect(() => {
    onDirtyChange?.(isDirty, () => handleSaveRef.current());
  }, [isDirty, onDirtyChange]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- colour tags ---------------------------------------------------------------------

  const openTagDialog = (tag) => {
    setTagError("");
    setTagDialog({ tag: tag || null, form: tag ? { ...tag } : { ...emptyTagForm } });
  };

  const saveTag = async () => {
    const { tag, form } = tagDialog;
    const payload = {
      name: (form.name || "").trim(),
      color: form.color,
      display_mode: form.display_mode,
      is_active: form.is_active,
    };
    try {
      setTagSaving(true);
      setTagError("");
      if (tag) {
        await axios.patch(`/api/color-tags/${tag.id}/?organization=${orgId}`, payload);
      } else {
        await axios.post(`/api/color-tags/?organization=${orgId}`, {
          ...payload,
          display_order: tags.length,
        });
      }
      setTagDialog(null);
      await loadTags();
      setSnackbar({ open: true, message: t("calendarSettings.tags.saved"), severity: "success" });
    } catch (error) {
      const data = error.response?.data;
      const fieldError = data?.name?.[0] || data?.color?.[0] || data?.display_mode?.[0];
      setTagError(fieldError || t("calendarSettings.tags.saveFailed"));
    } finally {
      setTagSaving(false);
    }
  };

  const toggleTagActive = async (tag) => {
    try {
      await axios.patch(`/api/color-tags/${tag.id}/?organization=${orgId}`, { is_active: !tag.is_active });
      await loadTags();
    } catch (error) {
      setSnackbar({ open: true, message: t("calendarSettings.tags.saveFailed"), severity: "error" });
    }
  };

  const moveTag = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= tags.length) return;
    const reordered = [...tags];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    setTags(reordered);
    try {
      await Promise.all(
        reordered.map((tag, i) =>
          axios.patch(`/api/color-tags/${tag.id}/?organization=${orgId}`, { display_order: i })
        )
      );
      await loadTags();
    } catch (error) {
      setSnackbar({ open: true, message: t("calendarSettings.tags.saveFailed"), severity: "error" });
      await loadTags();
    }
  };

  const deleteTag = async () => {
    try {
      await axios.delete(`/api/color-tags/${deleteTarget.id}/?organization=${orgId}`);
      setDeleteTarget(null);
      await loadTags();
      setSnackbar({ open: true, message: t("calendarSettings.tags.deleted"), severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: t("calendarSettings.tags.saveFailed"), severity: "error" });
    }
  };

  const tagSwatch = (tag) => (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: "4px",
        bgcolor: tag.display_mode === TAG_MODE_BORDER ? "transparent" : tag.color,
        border: tag.display_mode === TAG_MODE_BORDER ? `3px solid ${tag.color}` : "1px solid rgba(0,0,0,0.1)",
      }}
    />
  );

  const columns = [
    {
      key: "color",
      label: t("calendarSettings.tags.color"),
      width: "1%",
      render: (tag) => tagSwatch(tag),
    },
    {
      key: "name",
      label: t("calendarSettings.tags.name"),
      maxWidth: 260,
      render: (tag) => (
        <Typography variant="body2" sx={{ fontWeight: 500, opacity: tag.is_active ? 1 : 0.5 }}>
          {tag.name}
        </Typography>
      ),
    },
    {
      key: "display_mode",
      label: t("calendarSettings.tags.mode"),
      width: "1%",
      noWrap: true,
      render: (tag) => (
        <Chip
          size="small"
          variant="outlined"
          label={
            tag.display_mode === TAG_MODE_BORDER
              ? t("calendarSettings.tags.modeBorder")
              : t("calendarSettings.tags.modeFill")
          }
        />
      ),
    },
    {
      key: "usage_count",
      label: t("calendarSettings.tags.usage"),
      width: "1%",
      noWrap: true,
      render: (tag) => <Typography variant="body2">{tag.usage_count ?? 0}</Typography>,
    },
    {
      key: "is_active",
      label: t("calendarSettings.tags.active"),
      width: "1%",
      render: (tag) => (
        <Switch
          size="small"
          checked={!!tag.is_active}
          onChange={() => toggleTagActive(tag)}
          inputProps={{ "data-testid": `color-tag-active-${tag.id}` }}
        />
      ),
    },
    {
      key: "actions",
      label: "",
      width: "1%",
      noWrap: true,
      render: (tag, index) => (
        <Box sx={{ display: "flex", gap: 0.25 }}>
          <IconButton size="small" disabled={index === 0} onClick={() => moveTag(index, -1)}>
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" disabled={index === tags.length - 1} onClick={() => moveTag(index, 1)}>
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => openTagDialog(tag)} data-testid={`color-tag-edit-${tag.id}`}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => setDeleteTarget(tag)} data-testid={`color-tag-delete-${tag.id}`}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const employeeColumns = [
    {
      key: "position",
      label: t("calendarSettings.employeeOrder.position"),
      width: "1%",
      noWrap: true,
      render: (employee, index) => <Typography variant="body2">{index + 1}.</Typography>,
    },
    {
      key: "name",
      label: t("calendarSettings.employeeOrder.name"),
      maxWidth: 260,
      render: (employee) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {employee.name}
        </Typography>
      ),
    },
    {
      key: "title",
      label: t("calendarSettings.employeeOrder.jobTitle"),
      maxWidth: 200,
      render: (employee) => (
        <Typography variant="body2" color="text.secondary">
          {employee.title || ""}
        </Typography>
      ),
    },
    {
      key: "actions",
      label: "",
      width: "1%",
      noWrap: true,
      render: (employee, index) => (
        <Box sx={{ display: "flex", gap: 0.25 }}>
          <IconButton
            size="small"
            disabled={index === 0 || reordering}
            onClick={() => moveEmployee(index, -1)}
            data-testid={`employee-order-up-${employee.id}`}
          >
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={index === calendarEmployees.length - 1 || reordering}
            onClick={() => moveEmployee(index, 1)}
            data-testid={`employee-order-down-${employee.id}`}
          >
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (!currentOrganization) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900 }}>
      {/* --- state colours --- */}
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        {t("calendarSettings.stateColors.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("calendarSettings.stateColors.help")}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mb: 2 }}>
        {CALENDAR_STATE_KEYS.map((state) => {
          const config = getStateConfig(state, colors);
          const isOverridden = !!colors[state];
          return (
            <Box
              key={state}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                flexWrap: "wrap",
                pb: 2,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Box sx={{ minWidth: 190 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t(`status.${state}`)}
                </Typography>
                <Box
                  data-testid={`calendar-state-color-${state}-preview`}
                  sx={{
                    mt: 0.75,
                    px: 1,
                    py: 0.5,
                    borderRadius: "6px",
                    display: "inline-block",
                    fontSize: "12px",
                    fontWeight: 600,
                    bgcolor: config.bg,
                    color: config.text,
                    borderLeft: `3px solid ${config.color}`,
                  }}
                >
                  {t("calendarSettings.stateColors.preview")}
                </Box>
              </Box>
              <ColorSwatchInput
                value={colors[state] || STATUS_CONFIG[state]?.color || "#000000"}
                onChange={(hex) => setStateColor(state, hex)}
                testIdPrefix={`calendar-state-color-${state}`}
                onReset={isOverridden ? () => resetStateColor(state) : undefined}
              />
            </Box>
          );
        })}
      </Box>

      <Button
        variant="contained"
        onClick={handleSaveColors}
        disabled={!isDirty || saving}
        data-testid="calendar-state-colors-save"
      >
        {saving ? <CircularProgress size={20} /> : t("Save")}
      </Button>

      {/* --- colour tags --- */}
      <Box sx={{ mt: 5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {t("calendarSettings.tags.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("calendarSettings.tags.help")}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => openTagDialog(null)}
            data-testid="color-tag-add-button"
          >
            {t("calendarSettings.tags.add")}
          </Button>
        </Box>

        <Box sx={{ mt: 2 }}>
          <DataTable
            columns={columns}
            data={tags}
            loading={tagsLoading}
            alwaysVisible
            tableTestId="color-tags-table"
            testIdPrefix="color-tag-row"
            emptyTitle={t("calendarSettings.tags.empty")}
          />
        </Box>
      </Box>

      {/* --- employee column order --- */}
      {canManageTeam && (
        <Box sx={{ mt: 5 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {t("calendarSettings.employeeOrder.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("calendarSettings.employeeOrder.help")}
          </Typography>

          <Box sx={{ mt: 2 }}>
            <DataTable
              columns={employeeColumns}
              data={calendarEmployees}
              loading={employeesLoading}
              alwaysVisible
              tableTestId="employee-order-table"
              testIdPrefix="employee-order-row"
              emptyTitle={t("calendarSettings.employeeOrder.empty")}
            />
          </Box>
        </Box>
      )}

      {/* --- tag dialog --- */}
      <Dialog open={!!tagDialog} onClose={() => setTagDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle data-testid="color-tag-dialog">
          {tagDialog?.tag ? t("calendarSettings.tags.edit") : t("calendarSettings.tags.add")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label={t("calendarSettings.tags.name")}
              value={tagDialog?.form.name || ""}
              error={!!tagError}
              helperText={tagError}
              inputProps={{ maxLength: 50, "data-testid": "color-tag-name-input" }}
              onChange={(e) =>
                setTagDialog((prev) => ({ ...prev, form: { ...prev.form, name: e.target.value } }))
              }
            />

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {t("calendarSettings.tags.color")}
              </Typography>
              <ColorSwatchInput
                value={tagDialog?.form.color}
                onChange={(hex) =>
                  setTagDialog((prev) => ({ ...prev, form: { ...prev.form, color: hex } }))
                }
                testIdPrefix="color-tag-color"
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {t("calendarSettings.tags.mode")}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={tagDialog?.form.display_mode || TAG_MODE_FILL}
                onChange={(e, value) =>
                  value &&
                  setTagDialog((prev) => ({ ...prev, form: { ...prev.form, display_mode: value } }))
                }
              >
                <ToggleButton value={TAG_MODE_FILL} data-testid="color-tag-mode-fill">
                  {t("calendarSettings.tags.modeFill")}
                </ToggleButton>
                <ToggleButton value={TAG_MODE_BORDER} data-testid="color-tag-mode-border">
                  {t("calendarSettings.tags.modeBorder")}
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Preview of how a card will look in the chosen mode */}
              <Box
                data-testid="color-tag-preview"
                sx={{
                  mt: 1.5,
                  px: 1.25,
                  py: 1,
                  width: 170,
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  ...numFont,
                  bgcolor:
                    tagDialog?.form.display_mode === TAG_MODE_BORDER
                      ? STATUS_CONFIG.planned.bg
                      : tagDialog?.form.color,
                  color:
                    tagDialog?.form.display_mode === TAG_MODE_BORDER
                      ? STATUS_CONFIG.planned.text
                      : "#ffffff",
                  borderLeft: `${tagDialog?.form.display_mode === TAG_MODE_BORDER ? 5 : 3}px solid ${
                    STATUS_CONFIG.planned.color
                  }`,
                  outline:
                    tagDialog?.form.display_mode === TAG_MODE_BORDER
                      ? `2px solid ${tagDialog?.form.color}`
                      : "none",
                  outlineOffset: "-2px",
                }}
              >
                ABC-123
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={!!tagDialog?.form.is_active}
                  onChange={(e) =>
                    setTagDialog((prev) => ({ ...prev, form: { ...prev.form, is_active: e.target.checked } }))
                  }
                  inputProps={{ "data-testid": "color-tag-active-input" }}
                />
              }
              label={t("calendarSettings.tags.active")}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagDialog(null)} data-testid="color-tag-cancel">
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={saveTag}
            disabled={tagSaving || !(tagDialog?.form.name || "").trim()}
            data-testid="color-tag-save"
          >
            {tagSaving ? <CircularProgress size={20} /> : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- delete confirmation --- */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle data-testid="color-tag-delete-dialog">
          {t("calendarSettings.tags.deleteTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget?.usage_count
              ? t("calendarSettings.tags.deleteConfirm", { count: deleteTarget.usage_count })
              : t("calendarSettings.tags.deleteConfirmUnused")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t("Cancel")}</Button>
          <Button color="error" variant="contained" onClick={deleteTag} data-testid="color-tag-delete-confirm">
            {t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withTranslation()(CalendarSettingsTab);
